import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import {
  Database, Edit2, Loader2, Settings, Newspaper, Target, Layers,
  Lightbulb, ClipboardList, BookOpen, ChevronLeft, Users, Grid3X3,
  Check, X, MapPin, ListTodo, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import {
  PendingChallengesDialog,
  PendingOrganizersDialog,
  PendingPlannersDialog,
  PendingChecklistsDialog,
  ManageContentDialog,
  EditCommunityItemDialog,
  PendingEditsDialog,
} from "@/components/MasterDatabase";
import { AdminGuidelinesDialog } from "@/components/GuidelinesDialog";
import { PendingFactSubmissions } from "@/components/admin/FactsManager";
import CommunityUpdatesManager from "@/components/admin/CommunityUpdatesManager";

const API = `${getApiUrl()}/api`;

export default function MasterDatabaseCommunity() {
  const navigate = useNavigate();

  // Pending counts
  const [pendingCounts, setPendingCounts] = useState({ facts: 0, challenges: 0, organizers: 0, planners: 0, templates: 0, checklists: 0 });
  const [loading, setLoading] = useState(true);

  // Pending data
  const [gamesWithPendingFacts, setGamesWithPendingFacts] = useState([]);
  const [pendingChallenges, setPendingChallenges] = useState([]);
  const [pendingOrganizers, setPendingOrganizers] = useState([]);
  const [pendingGamePlanners, setPendingGamePlanners] = useState([]);
  const [pendingTemplates, setPendingTemplates] = useState([]);
  const [pendingMaps, setPendingMaps] = useState([]);
  const [pendingChecklists, setPendingChecklists] = useState([]);

  // Dialog states
  const [pendingFactsDialogOpen, setPendingFactsDialogOpen] = useState(false);
  const [pendingChallengesDialogOpen, setPendingChallengesDialogOpen] = useState(false);
  const [pendingOrganizersDialogOpen, setPendingOrganizersDialogOpen] = useState(false);
  const [pendingGamePlannersDialogOpen, setPendingGamePlannersDialogOpen] = useState(false);
  const [pendingTemplatesDialogOpen, setPendingTemplatesDialogOpen] = useState(false);
  const [pendingMapsDialogOpen, setPendingMapsDialogOpen] = useState(false);
  const [pendingChecklistsDialogOpen, setPendingChecklistsDialogOpen] = useState(false);
  const [manageContentDialogOpen, setManageContentDialogOpen] = useState(false);
  const [pendingEditsDialogOpen, setPendingEditsDialogOpen] = useState(false);
  const [guidelinesDialogOpen, setGuidelinesDialogOpen] = useState(false);
  const [communityUpdatesDialogOpen, setCommunityUpdatesDialogOpen] = useState(false);

  // Manage content state
  const [manageContentTab, setManageContentTab] = useState("challenges");
  const [manageContentLoading, setManageContentLoading] = useState(false);
  const [approvedChallenges, setApprovedChallenges] = useState([]);
  const [approvedOrganizers, setApprovedOrganizers] = useState([]);
  const [approvedFacts, setApprovedFacts] = useState([]);
  const [approvedChecklists, setApprovedChecklists] = useState([]);
  const [approvedGamePlanners, setApprovedGamePlanners] = useState([]);
  const [approvedGrids, setApprovedGrids] = useState([]);
  const [approvedMaps, setApprovedMaps] = useState([]);

  // Edit community item
  const [editCommunityItem, setEditCommunityItem] = useState(null);
  const [editCommunityItemType, setEditCommunityItemType] = useState(null);

  // Pending edits
  const [pendingEdits, setPendingEdits] = useState([]);
  const [loadingPendingEdits, setLoadingPendingEdits] = useState(false);

  // Fetch pending counts on load + whenever the tab regains focus / becomes visible
  // (covers the case where the user submits a checklist in another tab and returns here)
  useEffect(() => {
    fetchPendingData();
    const onFocus = () => fetchPendingData();
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchPendingData();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPendingData = async () => {
    setLoading(true);
    try {
      const [factsRes, challengesRes, organizersRes, plannersRes, templatesRes, mapsRes, checklistsRes] = await Promise.all([
        axios.get(`${API}/achievement-facts/admin/games-with-pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/community/admin/challenges/pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/community/admin/organizers/pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/community/admin/game-planners/pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/scorecards/admin/pending-templates`).catch(() => ({ data: [] })),
        axios.get(`${API}/community/admin/maps/pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/community/admin/checklists/pending`).catch(() => ({ data: [] })),
      ]);
      setGamesWithPendingFacts(factsRes.data || []);
      setPendingChallenges(challengesRes.data || []);
      setPendingOrganizers(organizersRes.data || []);
      setPendingGamePlanners(plannersRes.data || []);
      setPendingTemplates(templatesRes.data || []);
      setPendingMaps(mapsRes.data || []);
      setPendingChecklists(checklistsRes.data || []);
      setPendingCounts({
        facts: (factsRes.data || []).length,
        challenges: (challengesRes.data || []).length,
        organizers: (organizersRes.data || []).length,
        planners: (plannersRes.data || []).length,
        templates: (templatesRes.data || []).length,
        maps: (mapsRes.data || []).length,
        checklists: (checklistsRes.data || []).length,
      });
    } catch (error) {
      console.error("Failed to fetch pending data:", error);
    } finally {
      setLoading(false);
    }
  };

  const openManageContent = async () => {
    setManageContentDialogOpen(true);
    setManageContentLoading(true);
    try {
      const [challengesRes, organizersRes, factsRes, plannersRes, checklistsRes, gridsRes, mapsRes] = await Promise.all([
        axios.get(`${API}/community/challenges?status=approved`),
        axios.get(`${API}/community/organizers?status=approved`),
        axios.get(`${API}/achievement-facts/admin/all-facts`).catch(() => ({ data: [] })),
        axios.get(`${API}/community/game-planners?status=approved`).catch(() => ({ data: { planners: [] } })),
        axios.get(`${API}/admin/checklists`).catch(() => ({ data: { checklists: [] } })),
        axios.get(`${API}/scorecards/admin/approved-templates`).catch(() => ({ data: [] })),
        axios.get(`${API}/community/maps?sort=recent&limit=200`).catch(() => ({ data: { maps: [] } })),
      ]);
      setApprovedChallenges(challengesRes.data?.challenges || []);
      setApprovedOrganizers(organizersRes.data?.organizers || []);
      setApprovedFacts(factsRes.data || []);
      setApprovedGamePlanners(plannersRes.data?.planners || []);
      setApprovedChecklists(checklistsRes.data?.checklists || []);
      setApprovedGrids(gridsRes.data || []);
      setApprovedMaps(mapsRes.data?.maps || []);
    } catch (err) {
      console.error("Failed to fetch approved content:", err);
    } finally {
      setManageContentLoading(false);
    }
  };

  const openPendingEdits = async () => {
    setPendingEditsDialogOpen(true);
    setLoadingPendingEdits(true);
    try {
      const res = await axios.get(`${API}/community/admin/pending-edits`);
      setPendingEdits(res.data || []);
    } catch (err) {
      console.error("Failed to fetch pending edits:", err);
      toast.error("Failed to fetch pending edits");
    } finally {
      setLoadingPendingEdits(false);
    }
  };

  const totalPending = pendingCounts.facts + pendingCounts.challenges + pendingCounts.organizers + pendingCounts.planners + pendingCounts.templates + (pendingCounts.maps || 0) + (pendingCounts.checklists || 0);

  const sections = [
    {
      title: "Review Queue",
      description: "Approve or reject user submissions",
      items: [
        { label: "Pending Challenges", icon: Target, count: pendingCounts.challenges, color: "orange", onClick: () => setPendingChallengesDialogOpen(true) },
        { label: "Pending Organizers", icon: Layers, count: pendingCounts.organizers, color: "cyan", onClick: () => setPendingOrganizersDialogOpen(true) },
        { label: "Pending Planners", icon: ClipboardList, count: pendingCounts.planners, color: "indigo", onClick: () => setPendingGamePlannersDialogOpen(true) },
        { label: "Pending Facts", icon: Lightbulb, count: pendingCounts.facts, color: "amber", onClick: () => setPendingFactsDialogOpen(true) },
        { label: "Pending Templates", icon: Grid3X3, count: pendingCounts.templates, color: "purple", onClick: () => setPendingTemplatesDialogOpen(true) },
        { label: "Pending Maps", icon: MapPin, count: pendingCounts.maps || 0, color: "amber", onClick: () => setPendingMapsDialogOpen(true) },
        { label: "Pending Checklists", icon: ListTodo, count: pendingCounts.checklists || 0, color: "pink", onClick: () => { fetchPendingData(); setPendingChecklistsDialogOpen(true); } },
        { label: "Pending Edits", icon: Edit2, count: null, color: "amber", onClick: openPendingEdits },
      ],
    },
    {
      title: "Content Management",
      description: "Manage approved community content and settings",
      items: [
        { label: "Manage Content", icon: Settings, count: null, color: "green", onClick: openManageContent },
        { label: "Community Updates", icon: Newspaper, count: null, color: "purple", onClick: () => setCommunityUpdatesDialogOpen(true) },
        { label: "Guidelines", icon: BookOpen, count: null, color: "emerald", onClick: () => setGuidelinesDialogOpen(true) },
      ],
    },
  ];

  const colorMap = {
    orange: { bg: "bg-orange-500/10", border: "border-orange-500/20", text: "text-orange-400", badge: "bg-orange-500" },
    cyan: { bg: "bg-cyan-500/10", border: "border-cyan-500/20", text: "text-cyan-400", badge: "bg-cyan-500" },
    indigo: { bg: "bg-indigo-500/10", border: "border-indigo-500/20", text: "text-indigo-400", badge: "bg-indigo-500" },
    amber: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400", badge: "bg-amber-500" },
    green: { bg: "bg-green-500/10", border: "border-green-500/20", text: "text-green-400", badge: "bg-green-500" },
    purple: { bg: "bg-purple-500/10", border: "border-purple-500/20", text: "text-purple-400", badge: "bg-purple-500" },
    emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400", badge: "bg-emerald-500" },
    pink: { bg: "bg-pink-500/10", border: "border-pink-500/20", text: "text-pink-400", badge: "bg-pink-500" },
  };

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-6" data-testid="community-management-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/master-database")}
          className="text-white/60 hover:text-white"
          data-testid="back-to-master-db"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
            <Users className="w-8 h-8 text-purple-400" />
            Community Management
          </h1>
          <p className="text-white/60 mt-1">
            {loading ? "Loading..." : totalPending > 0 ? `${totalPending} items awaiting review` : "No pending reviews"}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchPendingData}
          disabled={loading}
          className="bg-white/5 border-white/10 text-white/70 hover:text-white hover:bg-white/10"
          data-testid="community-refresh-btn"
          title="Refresh pending counts"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2 hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Sections */}
      <div className="space-y-8 max-w-4xl">
        {sections.map((section) => (
          <div key={section.title}>
            <h2 className="text-lg font-semibold text-white mb-1">{section.title}</h2>
            <p className="text-white/40 text-sm mb-4">{section.description}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {section.items.map((item) => {
                const colors = colorMap[item.color];
                return (
                  <button
                    key={item.label}
                    onClick={item.onClick}
                    className={`flex items-center gap-3 p-4 rounded-lg border ${colors.border} ${colors.bg} hover:opacity-80 transition-all text-left group`}
                    data-testid={`community-${item.label.toLowerCase().replace(/\s+/g, "-")}-btn`}
                  >
                    <item.icon className={`w-5 h-5 ${colors.text} flex-shrink-0`} />
                    <span className={`text-sm font-medium ${colors.text}`}>{item.label}</span>
                    {item.count > 0 && (
                      <span className={`ml-auto ${colors.badge} text-white text-xs font-bold px-2 py-0.5 rounded-full`}>
                        {item.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* ============ DIALOGS ============ */}

      {/* Pending Facts Dialog */}
      <Dialog open={pendingFactsDialogOpen} onOpenChange={setPendingFactsDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-400" />
              Pending Fact Reviews
            </DialogTitle>
            <DialogDescription>Review and approve user-submitted facts and trivia</DialogDescription>
          </DialogHeader>
          <PendingFactSubmissions onRefresh={fetchPendingData} />
        </DialogContent>
      </Dialog>

      {/* Community Updates Manager Dialog */}
      <Dialog open={communityUpdatesDialogOpen} onOpenChange={setCommunityUpdatesDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-purple-400" />
              Community Updates Manager
            </DialogTitle>
            <DialogDescription>Create and manage news updates for the Community page</DialogDescription>
          </DialogHeader>
          <CommunityUpdatesManager />
        </DialogContent>
      </Dialog>

      {/* Pending Challenges Dialog */}
      <PendingChallengesDialog
        open={pendingChallengesDialogOpen}
        onOpenChange={setPendingChallengesDialogOpen}
        pendingChallenges={pendingChallenges}
        onApprove={async (id) => {
          try {
            await axios.post(`${API}/community/admin/challenges/${id}/approve`);
            toast.success("Challenge approved!");
            setPendingChallenges(prev => prev.filter(c => c.id !== id));
            setPendingCounts(prev => ({ ...prev, challenges: prev.challenges - 1 }));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to approve");
          }
        }}
        onReject={async (id) => {
          try {
            await axios.post(`${API}/community/admin/challenges/${id}/reject`);
            toast.success("Challenge rejected");
            setPendingChallenges(prev => prev.filter(c => c.id !== id));
            setPendingCounts(prev => ({ ...prev, challenges: prev.challenges - 1 }));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to reject");
          }
        }}
        onEdit={(item, type) => { setEditCommunityItem(item); setEditCommunityItemType(type); }}
      />

      {/* Pending Organizers Dialog */}
      <PendingOrganizersDialog
        open={pendingOrganizersDialogOpen}
        onOpenChange={setPendingOrganizersDialogOpen}
        pendingOrganizers={pendingOrganizers}
        onApprove={async (id) => {
          try {
            await axios.post(`${API}/community/admin/organizers/${id}/approve`);
            toast.success("Organizer approved!");
            setPendingOrganizers(prev => prev.filter(o => o.id !== id));
            setPendingCounts(prev => ({ ...prev, organizers: prev.organizers - 1 }));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to approve");
          }
        }}
        onReject={async (id) => {
          try {
            await axios.post(`${API}/community/admin/organizers/${id}/reject`);
            toast.success("Organizer rejected");
            setPendingOrganizers(prev => prev.filter(o => o.id !== id));
            setPendingCounts(prev => ({ ...prev, organizers: prev.organizers - 1 }));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to reject");
          }
        }}
        onEdit={(item, type) => { setEditCommunityItem(item); setEditCommunityItemType(type); }}
      />

      {/* Pending Planners Dialog */}
      <PendingPlannersDialog
        open={pendingGamePlannersDialogOpen}
        onOpenChange={setPendingGamePlannersDialogOpen}
        pendingPlanners={pendingGamePlanners}
        onApprove={async (id) => {
          try {
            await axios.post(`${API}/community/admin/game-planners/${id}/review?approve=true`);
            toast.success("Planner approved!");
            setPendingGamePlanners(prev => prev.filter(p => p.id !== id));
            setPendingCounts(prev => ({ ...prev, planners: prev.planners - 1 }));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to approve");
          }
        }}
        onReject={async (id) => {
          try {
            await axios.post(`${API}/community/admin/game-planners/${id}/review?approve=false`);
            toast.success("Planner rejected");
            setPendingGamePlanners(prev => prev.filter(p => p.id !== id));
            setPendingCounts(prev => ({ ...prev, planners: prev.planners - 1 }));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to reject");
          }
        }}
        onEdit={(item, type) => { setEditCommunityItem(item); setEditCommunityItemType(type); }}
      />

      {/* Pending Checklists Dialog */}
      <PendingChecklistsDialog
        open={pendingChecklistsDialogOpen}
        onOpenChange={setPendingChecklistsDialogOpen}
        pendingChecklists={pendingChecklists}
        onApprove={async (id) => {
          try {
            await axios.post(`${API}/community/admin/checklists/${id}/review?approve=true`);
            toast.success("Checklist approved!");
            setPendingChecklists(prev => prev.filter(c => c.id !== id));
            setPendingCounts(prev => ({ ...prev, checklists: Math.max(0, (prev.checklists || 0) - 1) }));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to approve");
          }
        }}
        onReject={async (id) => {
          const reason = window.prompt("Reason for rejection (optional — will be sent to the submitter):", "");
          if (reason === null) return; // user clicked Cancel
          try {
            const url = `${API}/community/admin/checklists/${id}/review?approve=false${reason ? `&reason=${encodeURIComponent(reason)}` : ""}`;
            await axios.post(url);
            toast.success("Checklist rejected");
            setPendingChecklists(prev => prev.filter(c => c.id !== id));
            setPendingCounts(prev => ({ ...prev, checklists: Math.max(0, (prev.checklists || 0) - 1) }));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to reject");
          }
        }}
      />

      {/* Pending Templates Dialog */}
      <Dialog open={pendingTemplatesDialogOpen} onOpenChange={setPendingTemplatesDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="pending-templates-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Grid3X3 className="w-5 h-5 text-purple-400" />
              Pending Grid Templates ({pendingTemplates.length})
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Review community-submitted grid templates
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {pendingTemplates.length === 0 ? (
              <p className="text-center text-white/40 py-6 text-sm">No pending templates</p>
            ) : (
              pendingTemplates.map(t => (
                <div key={t.id} className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-4 space-y-2" data-testid={`pending-template-${t.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-white font-medium text-sm">{t.name}</h3>
                      {t.description && <p className="text-white/50 text-xs mt-0.5">{t.description}</p>}
                    </div>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/20">
                      Pending
                    </span>
                  </div>
                  <div className="text-white/40 text-xs flex items-center gap-3">
                    <span>by {t.submittedByUsername || "Unknown"}</span>
                    <span>{t.rows?.length || 0} rows x {t.cols?.length || 0} cols</span>
                    {t.createdAt && <span>{new Date(t.createdAt).toLocaleDateString()}</span>}
                  </div>
                  {/* Preview grid structure */}
                  {t.rows?.length > 0 && t.cols?.length > 0 && (
                    <div className="bg-zinc-800/40 rounded p-2 text-[10px] overflow-x-auto">
                      <table className="border-collapse w-full">
                        <thead>
                          <tr>
                            <th className="w-16" />
                            {t.cols.map(c => (
                              <th key={c.id} className="px-0.5 py-0.5 text-center">
                                {c.isDivider ? (
                                  <div className="w-[2px] h-3 mx-auto rounded" style={{ backgroundColor: c.color || "#3f3f46" }} />
                                ) : (
                                  <span className={`text-[10px] ${c.name ? "text-zinc-400" : "text-zinc-600 italic"}`}>{c.name || "Col"}</span>
                                )}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {t.rows.slice(0, 6).map(r => {
                            if (r.isDivider) {
                              return (
                                <tr key={r.id}><td colSpan={t.cols.length + 1} className="py-0.5">
                                  <div className="h-[1px] rounded" style={{ backgroundColor: r.color || "#3f3f46" }} />
                                </td></tr>
                              );
                            }
                            return (
                              <tr key={r.id}>
                                <td className="pr-1">
                                  <span className={`text-[10px] truncate ${r.name ? "" : "italic opacity-50"}`} style={{ color: r.color }}>{r.name || "Row"}</span>
                                </td>
                                {t.cols.map(c => {
                                  if (c.isDivider) {
                                    return <td key={c.id} className="px-0"><div className="w-[1px] h-4 mx-auto" style={{ backgroundColor: c.color || "#3f3f46" }} /></td>;
                                  }
                                  const cell = t.cellDefaults?.[`${r.id}:${c.id}`];
                                  const type = cell?.type || "checkbox";
                                  return (
                                    <td key={c.id} className="px-0.5 py-0.5 text-center">
                                      <span className={`text-[10px] ${
                                        type === "checkbox" ? (cell?.value ? "text-purple-400" : "text-zinc-700") :
                                        type === "number" ? "text-emerald-400" : "text-amber-400"
                                      }`}>
                                        {type === "checkbox" ? (cell?.value ? "\u2713" : "\u2610") :
                                         type === "number" ? (cell?.value || "0") :
                                         (cell?.value || "\u00B7")}
                                      </span>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {t.rows.length > 6 && <p className="text-zinc-600 text-center mt-1">+{t.rows.length - 6} more rows</p>}
                    </div>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-500 h-7 text-xs"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/scorecards/admin/templates/${t.id}/review`, { status: "approved" });
                          toast.success("Template approved!");
                          setPendingTemplates(prev => prev.filter(x => x.id !== t.id));
                          setPendingCounts(prev => ({ ...prev, templates: prev.templates - 1 }));
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Failed to approve");
                        }
                      }}
                      data-testid={`approve-template-${t.id}`}
                    >
                      <Check className="w-3 h-3 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:text-red-300 h-7 text-xs"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/scorecards/admin/templates/${t.id}/review`, { status: "rejected" });
                          toast.success("Template rejected");
                          setPendingTemplates(prev => prev.filter(x => x.id !== t.id));
                          setPendingCounts(prev => ({ ...prev, templates: prev.templates - 1 }));
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Failed to reject");
                        }
                      }}
                      data-testid={`reject-template-${t.id}`}
                    >
                      <X className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pending Maps Dialog */}
      <Dialog open={pendingMapsDialogOpen} onOpenChange={setPendingMapsDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="pending-maps-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-amber-400" />
              Pending Map Reviews ({pendingMaps.length})
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Review community-submitted map notes
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {pendingMaps.length === 0 ? (
              <p className="text-center text-white/40 py-6 text-sm">No pending maps</p>
            ) : (
              pendingMaps.map(m => (
                <div key={m.id} className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-4 space-y-2" data-testid={`pending-map-${m.id}`}>
                  <div className="flex items-start gap-3">
                    {m.mapImageUrl && (
                      <div className="w-24 h-16 rounded-md overflow-hidden border border-white/10 flex-shrink-0">
                        <img src={m.mapImageUrl} alt={m.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-white font-medium text-sm">{m.title}</h3>
                          {m.description && <p className="text-white/50 text-xs mt-0.5">{m.description}</p>}
                        </div>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/20 shrink-0">
                          Pending
                        </span>
                      </div>
                      <div className="text-white/40 text-xs flex items-center gap-3 mt-1">
                        <span>by {m.submittedByUsername || "Unknown"}</span>
                        <span>{m.gameName}</span>
                        <span>{(m.pins || []).length} pins</span>
                        {m.sectionName && <span>Section: {m.sectionName}</span>}
                      </div>
                      {(m.tags || []).length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {m.tags.map(t => (
                            <span key={t} className="text-[10px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full">{t}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-500 h-7 text-xs"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/community/admin/maps/${m.id}/approve`);
                          toast.success("Map approved!");
                          setPendingMaps(prev => prev.filter(x => x.id !== m.id));
                          setPendingCounts(prev => ({ ...prev, maps: (prev.maps || 1) - 1 }));
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Failed to approve");
                        }
                      }}
                      data-testid={`approve-map-${m.id}`}
                    >
                      <Check className="w-3 h-3 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:text-red-300 h-7 text-xs"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/community/admin/maps/${m.id}/reject`);
                          toast.success("Map rejected");
                          setPendingMaps(prev => prev.filter(x => x.id !== m.id));
                          setPendingCounts(prev => ({ ...prev, maps: (prev.maps || 1) - 1 }));
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Failed to reject");
                        }
                      }}
                      data-testid={`reject-map-${m.id}`}
                    >
                      <X className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Approved Content Dialog */}
      <ManageContentDialog
        open={manageContentDialogOpen}
        onOpenChange={setManageContentDialogOpen}
        activeTab={manageContentTab}
        onTabChange={setManageContentTab}
        loading={manageContentLoading}
        approvedChallenges={approvedChallenges}
        approvedOrganizers={approvedOrganizers}
        approvedFacts={approvedFacts}
        approvedChecklists={approvedChecklists}
        approvedGamePlanners={approvedGamePlanners}
        approvedGrids={approvedGrids}
        approvedMaps={approvedMaps}
        onDeleteChallenge={async (id, name) => {
          if (!window.confirm(`Delete challenge "${name}"?`)) return;
          try {
            await axios.delete(`${API}/community/admin/challenges/${id}`);
            toast.success("Challenge deleted");
            setApprovedChallenges(prev => prev.filter(c => c.id !== id));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete challenge");
          }
        }}
        onDeleteOrganizer={async (id, name) => {
          if (!window.confirm(`Delete organizer "${name}"?`)) return;
          try {
            await axios.delete(`${API}/community/admin/organizers/${id}`);
            toast.success("Organizer deleted");
            setApprovedOrganizers(prev => prev.filter(o => o.id !== id));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete");
          }
        }}
        onDeleteFact={async (id) => {
          if (!window.confirm("Delete this trivia fact?")) return;
          try {
            await axios.delete(`${API}/achievement-facts/admin/facts/${id}`);
            toast.success("Trivia deleted");
            setApprovedFacts(prev => prev.filter(f => f.id !== id));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete");
          }
        }}
        onDeleteChecklist={async (id, title) => {
          if (!window.confirm(`Delete checklist "${title}"?`)) return;
          try {
            await axios.delete(`${API}/admin/checklists/${id}`);
            toast.success("Checklist deleted");
            setApprovedChecklists(prev => prev.filter(c => c.id !== id));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete checklist");
          }
        }}
        onDeletePlanner={async (id, name) => {
          if (!window.confirm(`Delete planner "${name}"?`)) return;
          try {
            await axios.delete(`${API}/community/admin/game-planners/${id}`);
            toast.success("Planner deleted");
            setApprovedGamePlanners(prev => prev.filter(p => p.id !== id));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete planner");
          }
        }}
        onDeleteGrid={async (id, name) => {
          if (!window.confirm(`Delete grid "${name}"?`)) return;
          try {
            await axios.delete(`${API}/scorecards/admin/templates/${id}`);
            toast.success("Grid deleted");
            setApprovedGrids(prev => prev.filter(g => g.id !== id));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete grid");
          }
        }}
        onDeleteMap={async (id, title) => {
          if (!window.confirm(`Delete map "${title}"?`)) return;
          try {
            await axios.delete(`${API}/community/admin/maps/${id}`);
            toast.success("Map deleted");
            setApprovedMaps(prev => prev.filter(m => m.id !== id));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete map");
          }
        }}
        onUpdateTags={async (contentType, itemId, tags) => {
          try {
            await axios.put(`${API}/community/admin/${contentType}/${itemId}/tags`, { tags });
            toast.success("Tags updated");
            const setterMap = {
              challenges: setApprovedChallenges,
              organizers: setApprovedOrganizers,
              planners: setApprovedGamePlanners,
            };
            const setter = setterMap[contentType];
            if (setter) {
              setter(prev => prev.map(item => item.id === itemId ? { ...item, tags } : item));
            }
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to update tags");
            throw err;
          }
        }}
        onEdit={(item, type) => { setEditCommunityItem(item); setEditCommunityItemType(type); }}
      />

      {/* Edit Community Item Dialog */}
      <EditCommunityItemDialog
        open={!!editCommunityItem}
        onOpenChange={(open) => { if (!open) { setEditCommunityItem(null); setEditCommunityItemType(null); } }}
        item={editCommunityItem}
        itemType={editCommunityItemType}
        onSave={async (type, id, data) => {
          const endpointMap = { challenges: "challenges", organizers: "organizers", planners: "game-planners" };
          let res;
          if (type === "grids") {
            res = await axios.put(`${API}/scorecards/admin/templates/${id}`, data);
          } else {
            const endpoint = endpointMap[type];
            res = await axios.put(`${API}/community/admin/${endpoint}/${id}`, data);
          }
          toast.success("Item updated!");
          const pendingSetterMap = { challenges: setPendingChallenges, organizers: setPendingOrganizers, planners: setPendingGamePlanners };
          const approvedSetterMap = { challenges: setApprovedChallenges, organizers: setApprovedOrganizers, planners: setApprovedGamePlanners, grids: setApprovedGrids };
          const updated = res.data;
          pendingSetterMap[type]?.(prev => prev.map(item => item.id === id ? { ...item, ...updated } : item));
          approvedSetterMap[type]?.(prev => prev.map(item => item.id === id ? { ...item, ...updated } : item));
        }}
      />

      {/* Pending Edits Review Dialog */}
      <PendingEditsDialog
        open={pendingEditsDialogOpen}
        onOpenChange={setPendingEditsDialogOpen}
        pendingEdits={pendingEdits}
        loading={loadingPendingEdits}
        onApproveEdit={async (type, id) => {
          try {
            await axios.post(`${API}/community/admin/${type}/${id}/approve-edit`);
            toast.success("Edit approved and applied!");
            setPendingEdits(prev => prev.filter(item => item.id !== id));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to approve edit");
          }
        }}
        onRejectEdit={async (type, id) => {
          try {
            await axios.post(`${API}/community/admin/${type}/${id}/reject-edit`);
            toast.success("Edit rejected");
            setPendingEdits(prev => prev.filter(item => item.id !== id));
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to reject edit");
          }
        }}
      />

      {/* Admin Guidelines Dialog */}
      <AdminGuidelinesDialog
        open={guidelinesDialogOpen}
        onOpenChange={setGuidelinesDialogOpen}
      />
    </div>
  );
}
